import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  Store, 
  ShoppingCart, 
  Footprints, 
  Car, 
  CheckCircle,
  Gift,
  AlertCircle,
  Clock,
  Home
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";

export default function VisitSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { result, qrInfo } = location.state || {};

  useEffect(() => {
    // If no result data, redirect to home
    if (!result || !qrInfo) {
      navigate('/');
    }
  }, [result, qrInfo, navigate]);

  if (!result || !qrInfo) {
    return null;
  }

  const isPOS = result.visit_type === 'pos';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col" data-testid="visit-success-page">
      {/* Header with branding */}
      <header className="p-4 flex items-center justify-between border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-medium text-slate-400">ParkWise</span>
        </div>
        <span className="text-xs text-slate-500">Local Connect</span>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Success Card */}
        <Card className="w-full max-w-md bg-slate-800/80 border-slate-700">
          <CardContent className="pt-8 pb-8">
            {/* Success icon */}
            <div className="flex justify-center mb-6">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                result.is_rewardable 
                  ? 'bg-green-500/20' 
                  : result.is_duplicate 
                    ? 'bg-amber-500/20' 
                    : 'bg-blue-500/20'
              }`}>
                {result.is_rewardable ? (
                  <CheckCircle className="w-10 h-10 text-green-400" />
                ) : result.is_duplicate ? (
                  <Clock className="w-10 h-10 text-amber-400" />
                ) : (
                  <CheckCircle className="w-10 h-10 text-blue-400" />
                )}
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-white text-center mb-2" data-testid="success-title">
              {result.is_duplicate ? 'Visit Recorded' : 'Thank You!'}
            </h1>
            <p className="text-slate-400 text-center mb-6">
              {result.message}
            </p>

            {/* Visit details */}
            <div className="bg-slate-700/50 rounded-xl p-4 mb-6 space-y-3">
              {/* Retailer */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-600 flex items-center justify-center flex-shrink-0">
                  {result.retailer_logo ? (
                    <img 
                      src={result.retailer_logo} 
                      alt={result.retailer_name} 
                      className="w-6 h-6 object-contain"
                    />
                  ) : (
                    <Store className="w-5 h-5 text-slate-400" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-400">Retailer</p>
                  <p className="text-white font-medium" data-testid="success-retailer">{result.retailer_name}</p>
                </div>
              </div>

              {/* Site */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-600 flex items-center justify-center flex-shrink-0">
                  <Car className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Location</p>
                  <p className="text-white font-medium">{result.site_name}</p>
                </div>
              </div>

              {/* Visit type */}
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isPOS ? 'bg-green-500/20' : 'bg-purple-500/20'
                }`}>
                  {isPOS ? (
                    <ShoppingCart className="w-5 h-5 text-green-400" />
                  ) : (
                    <Footprints className="w-5 h-5 text-purple-400" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-400">Visit Type</p>
                  <p className={`font-medium ${isPOS ? 'text-green-400' : 'text-purple-400'}`} data-testid="success-visit-type">
                    {isPOS ? 'POS / Validation QR' : 'Browse QR'}
                  </p>
                </div>
              </div>
            </div>

            {/* Credit status */}
            {result.is_rewardable ? (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
                <Gift className="w-6 h-6 text-green-400 flex-shrink-0" />
                <div>
                  <p className="text-green-400 font-medium">
                    {isPOS && result.parking_entitlement_earned
                      ? 'Ayr Miles: 3 Hours Free Parking Earned!'
                      : result.credits_earned 
                        ? `+${result.credits_earned} Parking Credit${result.credits_earned !== 1 ? 's' : ''} Earned!`
                        : 'Parking Credit Earned!'}
                  </p>
                  <p className="text-sm text-slate-400">
                    {isPOS && result.parking_entitlement_earned
                      ? 'Valid same day only. Your first 3 hours are free!'
                      : 'Credit will be applied to your parking account'}
                  </p>
                </div>
              </div>
            ) : result.is_duplicate ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
                <Clock className="w-6 h-6 text-amber-400 flex-shrink-0" />
                <div>
                  <p className="text-amber-400 font-medium">Duplicate Scan Detected</p>
                  <p className="text-sm text-slate-400">Visit recorded but no additional credit applied</p>
                </div>
              </div>
            ) : result.is_anonymous ? (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-blue-400 flex-shrink-0" />
                <div>
                  <p className="text-blue-400 font-medium">Anonymous Visit</p>
                  <p className="text-sm text-slate-400">Register your vehicle to earn parking credits next time</p>
                </div>
              </div>
            ) : null}

            {/* Action buttons */}
            <div className="space-y-3">
              {result.is_anonymous && (
                <Button
                  onClick={() => navigate('/kiosk')}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                >
                  Register My Vehicle
                </Button>
              )}
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full h-12 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Home className="w-4 h-4 mr-2" />
                Done
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Timestamp */}
        <p className="text-xs text-slate-500 text-center mt-4">
          Visit ID: {result.visit_id}
        </p>
      </main>
    </div>
  );
}
